<script setup>
import { computed } from 'vue';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isReplyRestricted: {
    type: Boolean,
    default: false,
  },
  enableInteractive: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['setMode']);

const activeMode = computed(() =>
  props.isReplyRestricted ? REPLY_EDITOR_MODES.NOTE : props.mode
);

const buttonClass = mode => {
  const baseClass =
    'relative z-10 inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-all duration-150';
  if (activeMode.value !== mode) {
    return [
      baseClass,
      'text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12',
    ];
  }
  if (mode === REPLY_EDITOR_MODES.NOTE) {
    return [
      baseClass,
      'bg-n-amber-3 text-n-amber-12 shadow-sm ring-1 ring-n-amber-6/60',
    ];
  }
  return [baseClass, 'bg-n-brand text-white shadow-sm shadow-n-brand/20'];
};
</script>

<template>
  <div
    class="flex h-10 w-auto items-center gap-0.5 rounded-xl border border-n-weak bg-n-solid-2 p-1 shadow-inner shadow-n-slate-3/30"
    role="tablist"
  >
    <button
      type="button"
      role="tab"
      :aria-selected="activeMode === REPLY_EDITOR_MODES.REPLY"
      :disabled="disabled || isReplyRestricted"
      :class="buttonClass(REPLY_EDITOR_MODES.REPLY)"
      @click="$emit('setMode', REPLY_EDITOR_MODES.REPLY)"
    >
      <span class="i-lucide-message-circle size-3.5" />
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </button>
    <button
      type="button"
      role="tab"
      :aria-selected="activeMode === REPLY_EDITOR_MODES.NOTE"
      :disabled="disabled"
      :class="buttonClass(REPLY_EDITOR_MODES.NOTE)"
      @click="$emit('setMode', REPLY_EDITOR_MODES.NOTE)"
    >
      <span class="i-lucide-lock-keyhole size-3.5" />
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </button>
    <button
      v-if="enableInteractive"
      type="button"
      role="tab"
      :aria-selected="activeMode === REPLY_EDITOR_MODES.INTERACTIVE"
      :disabled="disabled || isReplyRestricted"
      :class="buttonClass(REPLY_EDITOR_MODES.INTERACTIVE)"
      @click="$emit('setMode', REPLY_EDITOR_MODES.INTERACTIVE)"
    >
      <span class="i-lucide-layout-template size-3.5" />
      {{ $t('CONVERSATION.REPLYBOX.INTERACTIVE') }}
    </button>
  </div>
</template>
